import React from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  StethoscopeIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ShieldIcon,
  AwardIcon,
  AlertTriangleIcon,
  LightbulbIcon,
  ActivityIcon,
  ZapIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { openWhatsApp } from "../../lib/utils";

const GallStone = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Gall Stone Treatment", href: "/gall-stone" }
  ];

  const stats = [
    { number: "95%", label: "Success Rate", icon: ShieldIcon },
    { number: "45min", label: "Procedure Time", icon: ClockIcon },
    { number: "24hrs", label: "Recovery Time", icon: ActivityIcon },
    { number: "500+", label: "Successful Cases", icon: AwardIcon }
  ];

  const causes = [
    "High cholesterol levels in bile",
    "Bile concentration due to prolonged fasting",
    "Rapid weight loss",
    "Diabetes and metabolic disorders",
    "Genetic predisposition",
    "Certain medications",
    "Pregnancy and hormonal changes",
    "Liver cirrhosis"
  ];

  const symptoms = [
    "Severe pain in upper right abdomen",
    "Pain between shoulder blades",
    "Nausea and vomiting",
    "Fever and chills",
    "Yellowing of skin (jaundice)",
    "Dark urine and pale stools",
    "Bloating and indigestion",
    "Sudden onset of pain after meals"
  ];

  const procedures = [
    {
      name: "Laparoscopic Cholecystectomy",
      description: "Minimally invasive removal of gallbladder through small incisions",
      duration: "45-60 minutes",
      recovery: "1-2 days",
      benefits: ["Less pain", "Faster recovery", "Minimal scarring", "Same-day discharge"]
    },
    {
      name: "Open Cholecystectomy",
      description: "Traditional surgical removal through larger incision",
      duration: "1-2 hours",
      recovery: "5-7 days",
      benefits: ["Complete removal", "Suitable for complex cases", "Proven technique"]
    },
    {
      name: "ERCP with Stone Removal",
      description: "Endoscopic procedure to remove stones from bile duct",
      duration: "30-45 minutes",
      recovery: "Same day",
      benefits: ["No incisions", "Quick recovery", "Bile duct clearance"]
    }
  ];

  const prevention = [
    "Maintain healthy weight gradually",
    "Eat regular, balanced meals",
    "Include fiber-rich foods",
    "Stay hydrated with plenty of water",
    "Limit saturated and trans fats",
    "Exercise regularly",
    "Avoid rapid weight loss diets",
    "Manage diabetes and cholesterol"
  ];

  const diagnosis = [
    "Blood tests (liver function, bilirubin)",
    "Ultrasound scan of abdomen",
    "CT scan for detailed imaging",
    "MRI/MRCP for bile duct stones",
    "Endoscopic ultrasound (EUS)",
    "HIDA scan for gallbladder function",
    "ERCP for diagnostic and therapeutic purposes"
  ];

  const gallStoneStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": "Gall Stone Treatment",
    "description": "Advanced gallstone treatment using minimally invasive laparoscopic techniques. Expert care for gallstone removal with modern surgical methods and comprehensive recovery support.",
    "procedureType": "Surgical Treatment",
    "bodyLocation": "Gallbladder",
    "preparation": "Pre-surgical consultation and preparation",
    "followup": "Post-operative care and recovery monitoring",
    "provider": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Delhi",
        "addressCountry": "IN"
      }
    },
    "medicalSpecialty": "General Surgery",
    "areaServed": ["Delhi", "Gurgaon", "Noida", "Faridabad"]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Gall Stone Treatment"
        description="Expert gallstone removal at TheLivCure. Advanced minimally invasive laparoscopic cholecystectomy. Experienced surgeons, modern facilities, 95% success rate. Book consultation today."
        keywords="gall stone treatment, gallstone surgery, laparoscopic cholecystectomy, gallstone removal Delhi, gallbladder surgery, gallstone doctor, gallstone specialist, gallstone treatment cost"
        url="/gall-stone"
        structuredData={gallStoneStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 sm:py-16 lg:py-20">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 bg-green-100">
          <video 
            src="/videos/gallStone_vid_1.mp4" 
            autoPlay 
            loop 
            muted 
            playsInline
            className="w-full h-full object-cover"
            style={{ minHeight: '100%', minWidth: '100%' }}
            onError={(e) => {
              console.log('Video error:', e);
              // Fallback to home page video if gallstone video fails
              e.target.src = '/videos/herosection_bg.mp4';
            }}
            onLoadStart={() => console.log('Video loading started')}
            onCanPlay={() => console.log('Video can play')}
          />
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-8">
            <div>
              <Badge className="bg-green-100 text-green-800 mb-4">General Surgery</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Advanced Gall Stone Treatment
              </h1>
              <p className="text-lg sm:text-xl text-gray-400 mb-6 leading-relaxed">
                Expert gallstone removal with minimally invasive laparoscopic techniques. 
                Get relief from gallstone pain with our advanced surgical procedures and 
                comprehensive care approach.
              </p>
              <p className="text-base text-gray-400 mb-8 leading-relaxed">
                Gallstones are hardened deposits that form in the gallbladder, a small organ beneath the liver that stores bile. 
                These stones can cause severe pain, inflammation, and complications if left untreated. Our experienced surgeons 
                use state-of-the-art laparoscopic techniques to safely remove gallstones with minimal scarring and faster recovery times.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <BookAppointmentModal>
                  <Button className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg">
                    Book Free Consultation
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </BookAppointmentModal>
                <Button 
                  variant="outline" 
                  className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-3 text-lg"
                  onClick={() => openWhatsApp("9311720045", "Hi, I need consultation for gall stone treatment")}
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-2xl p-8 text-white mb-8">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <stat.icon className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-2xl sm:text-3xl font-bold">{stat.number}</div>
                      <div className="text-green-100 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src="/image/gallStone_1.jpg" 
                    alt="Gallstone treatment illustration" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Modern laparoscopic gallstone removal procedure
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What are Gall Stones Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What are Gall Stones?</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Gallstones are small, hard deposits that form in the gallbladder, typically made of cholesterol or bilirubin. 
                They can vary in size from a grain of sand to a golf ball and may cause no symptoms or lead to severe complications.
              </p>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">
                The gallbladder is a small, pear-shaped organ located beneath the liver on the right side of the abdomen. 
                It stores bile, a digestive fluid produced by the liver. When bile contains too much cholesterol or bilirubin, 
                or when the gallbladder doesn't empty properly, gallstones can form.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Cholesterol Stones</h3>
                    <p className="text-gray-600">Most common type, formed when bile contains too much cholesterol</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Pigment Stones</h3>
                    <p className="text-gray-600">Formed from bilirubin, more common in people with liver disease</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/gallStone_2.jpg" 
                alt="Gallbladder anatomy" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Gallbladder anatomy and gallstone formation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms and Warning Signs Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Symptoms and Warning Signs</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Gallstone symptoms can vary widely, from mild discomfort to severe pain. 
              Recognizing these signs early can help prevent complications.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Severe Abdominal Pain</h3>
              <p className="text-sm text-gray-600">Intense pain in upper right abdomen that may radiate to back or shoulder</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangleIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Nausea & Vomiting</h3>
              <p className="text-sm text-gray-600">Persistent nausea and vomiting, especially after meals</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ActivityIcon className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Jaundice</h3>
              <p className="text-sm text-gray-600">Yellowing of skin and eyes due to bile duct obstruction</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Fever & Chills</h3>
              <p className="text-sm text-gray-600">Signs of infection or inflammation in the gallbladder</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Our Treatment Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Choose Our Gall Stone Treatment?</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our comprehensive approach to gallstone treatment combines advanced surgical techniques 
                with personalized care to ensure the best possible outcomes for our patients.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ShieldIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Experienced Surgeons</h3>
                    <p className="text-gray-600">Our team consists of highly trained surgeons with extensive experience in laparoscopic procedures</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ZapIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Advanced Technology</h3>
                    <p className="text-gray-600">State-of-the-art laparoscopic equipment for minimally invasive procedures</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <AwardIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">High Success Rate</h3>
                    <p className="text-gray-600">95% success rate with minimal complications and excellent patient satisfaction</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/gallStone_3.jpg" 
                alt="Medical team performing surgery" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Our expert surgical team in action
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Details */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="causes">Causes</TabsTrigger>
              <TabsTrigger value="symptoms">Symptoms</TabsTrigger>
              <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
              <TabsTrigger value="procedures">Procedures</TabsTrigger>
              <TabsTrigger value="prevention">Prevention</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <StethoscopeIcon className="w-6 h-6 mr-2 text-green-600" />
                      Treatment Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-6">
                      Gallstones are hardened deposits that form in the gallbladder, a small organ 
                      that stores bile. Our advanced treatment options include minimally invasive 
                      laparoscopic cholecystectomy, which offers faster recovery and minimal scarring.
                    </p>
                    <p className="text-gray-600 mb-6">
                      The gallbladder is a small, pear-shaped organ located beneath the liver that 
                      concentrates and stores bile. When gallstones form, they can block the flow 
                      of bile, causing pain and inflammation. Our comprehensive treatment approach 
                      ensures optimal outcomes with minimal discomfort.
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AwardIcon className="w-6 h-6 mr-2 text-green-600" />
                      Why Choose Us
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Experienced Surgeons</h4>
                          <p className="text-sm text-gray-600">Board-certified surgeons with extensive gallstone treatment experience</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Advanced Technology</h4>
                          <p className="text-sm text-gray-600">State-of-the-art laparoscopic equipment for precise procedures</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Comprehensive Care</h4>
                          <p className="text-sm text-gray-600">Complete pre and post-operative care with dedicated support</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <ClockIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Quick Recovery</h3>
                  <p className="text-sm text-gray-600">Most patients return to normal activities within 1-2 weeks</p>
                </div>
                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <ShieldIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Safe Procedure</h3>
                  <p className="text-sm text-gray-600">95% success rate with minimal complications</p>
                </div>
                <div className="text-center p-6 bg-green-50 rounded-lg sm:col-span-2 lg:col-span-1">
                  <ZapIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h3 className="font-semibold mb-2">Minimally Invasive</h3>
                  <p className="text-sm text-gray-600">Small incisions for faster healing and less pain</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="causes" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AlertTriangleIcon className="w-6 h-6 mr-2 text-orange-600" />
                      Primary Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {causes.slice(0, 4).map((cause, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{cause}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                      Additional Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {causes.slice(4).map((cause, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{cause}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                    Understanding Gall Stone Formation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-orange-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Bile Imbalance</h4>
                      <p className="text-sm text-gray-600">Excess cholesterol or insufficient bile salts cause crystallization</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-orange-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Gallbladder Stasis</h4>
                      <p className="text-sm text-gray-600">Slow emptying allows bile to concentrate and form stones</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-orange-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Stone Growth</h4>
                      <p className="text-sm text-gray-600">Crystals grow larger over time, becoming problematic stones</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="symptoms" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                      Common Symptoms
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {symptoms.slice(0, 4).map((symptom, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{symptom}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <AlertTriangleIcon className="w-6 h-6 mr-2 text-orange-600" />
                      Advanced Symptoms
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {symptoms.slice(4).map((symptom, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{symptom}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ActivityIcon className="w-6 h-6 mr-2 text-purple-600" />
                    When to Seek Emergency Care
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div className="flex items-start">
                      <AlertTriangleIcon className="w-6 h-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-red-900 mb-2">Seek Immediate Medical Attention If You Experience:</h4>
                        <ul className="space-y-2 text-red-800">
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-red-600 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                            Severe abdominal pain lasting more than 6 hours
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-red-600 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                            High fever (above 101°F) with chills
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-red-600 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                            Yellowing of skin or eyes (jaundice)
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-red-600 rounded-full mr-2 mt-2 flex-shrink-0"></span>
                            Persistent nausea and vomiting
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="diagnosis" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <ActivityIcon className="w-6 h-6 mr-2 text-blue-600" />
                      Initial Diagnostic Tests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {diagnosis.slice(0, 4).map((test, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{test}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <ZapIcon className="w-6 h-6 mr-2 text-green-600" />
                      Advanced Diagnostic Tests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {diagnosis.slice(4).map((test, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{test}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <StethoscopeIcon className="w-6 h-6 mr-2 text-purple-600" />
                    Diagnostic Process Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Medical History</h4>
                      <p className="text-sm text-gray-600">Detailed discussion of symptoms and risk factors</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Physical Exam</h4>
                      <p className="text-sm text-gray-600">Abdominal examination to check for tenderness</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Imaging Tests</h4>
                      <p className="text-sm text-gray-600">Ultrasound, CT scan, or MRI to visualize stones</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-blue-600 font-bold text-lg">4</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Treatment Plan</h4>
                      <p className="text-sm text-gray-600">Personalized treatment based on findings</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="procedures" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {procedures.map((procedure, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{procedure.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{procedure.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Duration:</span>
                          <span className="text-sm font-medium">{procedure.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Recovery:</span>
                          <span className="text-sm font-medium">{procedure.recovery}</span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-2">Benefits:</h4>
                        <ul className="space-y-1">
                          {procedure.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ShieldIcon className="w-6 h-6 mr-2 text-green-600" />
                    Surgical Process Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-green-600 font-bold text-lg">1</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Pre-Surgery</h4>
                      <p className="text-sm text-gray-600">Fasting, anesthesia preparation, and final consultations</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-green-600 font-bold text-lg">2</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Surgery</h4>
                      <p className="text-sm text-gray-600">Minimally invasive procedure with small incisions</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-green-600 font-bold text-lg">3</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Recovery</h4>
                      <p className="text-sm text-gray-600">Monitoring and pain management in recovery room</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-green-600 font-bold text-lg">4</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Discharge</h4>
                      <p className="text-sm text-gray-600">Same-day or next-day discharge with care instructions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="prevention" className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                      Lifestyle Prevention
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {prevention.slice(0, 4).map((tip, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <HeartIcon className="w-6 h-6 mr-2 text-green-600" />
                      Medical Prevention
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {prevention.slice(4).map((tip, index) => (
                        <div key={index} className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ShieldIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Prevention Strategy Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-yellow-600 font-bold text-lg">🍎</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Healthy Diet</h4>
                      <p className="text-sm text-gray-600">High fiber, low fat, and regular meal timing</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-yellow-600 font-bold text-lg">💧</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Stay Hydrated</h4>
                      <p className="text-sm text-gray-600">Drink plenty of water to maintain bile flow</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <span className="text-yellow-600 font-bold text-lg">⚖️</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Weight Management</h4>
                      <p className="text-sm text-gray-600">Maintain healthy weight and avoid rapid weight loss</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Gall Stone Treatment Gallery</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See our advanced gallstone treatment facilities and procedures in action. 
              Our modern equipment and expert care ensure the best outcomes for our patients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/gallStone_1.jpg" 
                alt="Gallstone treatment procedure overview" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Overview</h3>
                <p className="text-sm text-gray-600">Advanced minimally invasive gallstone removal procedures</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/gallStone_2.jpg" 
                alt="Gallbladder anatomy and diagnosis" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Anatomy & Diagnosis</h3>
                <p className="text-sm text-gray-600">Understanding gallbladder anatomy and gallstone formation</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/gallStone_3.jpg" 
                alt="Modern surgical equipment" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Modern Equipment</h3>
                <p className="text-sm text-gray-600">State-of-the-art laparoscopic surgical equipment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-green-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Relief from Gall Stone Pain?
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Book your consultation today and take the first step towards a pain-free life.
          </p>
          <BookAppointmentModal>
            <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-3 text-lg">
              Book Free Consultation
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </BookAppointmentModal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GallStone;
